import React from 'react'
import { Box, Chip, IconButton, Link, Tooltip } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { IconSeeker } from '@ui-seeker/core'

import { dsColorNew } from '@palette'
import { toneChipSx } from 'Pages/DsRepricing/@components/tones'
import { pct, points, tone } from '@utils/colourScale'
import { cellInk } from 'Pages/BusinessLogistics/Dashboards/OfficeNetwork/Page/@components/DistanceCell'
import { diffInUnit, inUnit, money, signedMoney } from '../@services/amounts'
import { activityHref, executionsHref } from '../@services/viewState'
import { fmtInstant, shortPass } from '../@services/windowLabel'
import { resolutionStyle } from './resolution'


/** The pass of a push: its time in the account zone and its id, the id linking to Executions. */
export function PassCell({ push, tz }) {
	return <Box sx={{ whiteSpace: 'nowrap' }}>
		<Box sx={{ fontSize: 12.5, color: dsColorNew.textTitle }}>{fmtInstant(push.ts, tz)}</Box>
		<Link component={RouterLink} to={executionsHref(push.pass)} title={`${push.pass} — open this pass in Executions`} sx={{ fontSize: 11.5, color: dsColorNew.appBlue, fontFamily: 'monospace' }}>{shortPass(push.pass)}</Link>
	</Box>
}

/** `149.09 € → 150.18 €` in the page's unit. */
export function PairCell({ before, after, unit, lor }) {
	return <Box sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{money(inUnit(before, unit, lor))} → {money(inUnit(after, unit, lor))}</Box>
}

/** `▼ −2.6%` in the BI ink, `—` without a figure. */
export function RatioCell({ ratio }) {
	const t = tone(ratio)
	if (!t) return <Box component="span" sx={{ color: dsColorNew.textSubtle }}>—</Box>
	return <Box component="span" sx={{ color: cellInk(t), fontWeight: 600, whiteSpace: 'nowrap' }}>{t.arrow} {pct(ratio)}</Box>
}

/** `▼ −2.6%` and, under it, the same move in euros in the page's unit (Total / Per day), as "Movement by ACRISS". */
export function ChangeMoneyCell({ ratio, before, after, unit, lor }) {
	const diff = diffInUnit(before, after, unit, lor)
	return <Box sx={{ whiteSpace: 'nowrap' }}>
		<RatioCell ratio={ratio}/>
		{diff != null && ratio != null && <Box component="span" sx={{ display: 'block', fontSize: 11, color: dsColorNew.textMuted, fontVariantNumeric: 'tabular-nums' }}>{signedMoney(diff)}</Box>}
	</Box>
}

/**
 * "Observed next pass" (phase 2, TL R6): the shelf price the next pass of the same cell read, beside the target the
 * engine set ("after"); `—` on the newest pass of a cell. Amber when it differs from the target by a cent or more.
 * v1.5: a simulated decision that counts has none (the server sends null) — nothing was written, the shelf never moved.
 */
export function ObservedCell({ observed, target, unit, lor, simulated = false }) {
	const o = inUnit(observed, unit, lor)
	if (o === null && simulated) return <Tooltip title="Simulated: nothing was written to the PMS, so the next pass has nothing to compare"><Box component="span" sx={{ color: dsColorNew.textSubtle }}>—</Box></Tooltip>
	if (o === null) return <Tooltip title="No later pass of this cell in the window"><Box component="span" sx={{ color: dsColorNew.textSubtle }}>—</Box></Tooltip>
	const t = inUnit(target, unit, lor)
	const off = t !== null && Math.abs(o - t) >= 0.01
	return <Tooltip title={off ? 'The next pass read a different price on the site than the target set here' : 'The next pass read the target set here'}>
		<Box component="span" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: off ? dsColorNew.noteAmber : dsColorNew.textDark, fontWeight: off ? 600 : 400 }}>{money(o)}</Box>
	</Tooltip>
}

export function PointsCell({ pt }) {
	if (pt == null) return <Box component="span" sx={{ color: dsColorNew.textSubtle }}>—</Box>
	return <Box component="span" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{points(pt)}</Box>
}

export function ResolutionChip({ push }) {
	const s = resolutionStyle(push)
	return <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
		<Chip size="small" label={s.label} sx={toneChipSx(s.tone)}/>
		{push.sim && push.resolution !== 'simulated' && <Chip size="small" label="SIM" sx={toneChipSx('accent')}/>}
	</Box>
}

/** "Open in Activity" of one push: the pass, office, ACRISS, pick-up and length, in a new tab. */
export function OpenInActivity({ push, view, label = 'Open in Activity' }) {
	const href = activityHref({ execution_id: push.pass, office: view.office, sipp: push.sipp, pickup_from: push.pickup_date, lor: view.lor })
	return <Tooltip title={`${label}: this pass, ${push.sipp}, ${push.pickup_date}, ${view.lor} days`}>
		<IconButton component="a" href={href} target="_blank" rel="noopener" size="small" aria-label={label} sx={{ color: dsColorNew.appBlue }}>
			<IconSeeker icon="arrow-up-right-from-square" sx={{ fontSize: 13 }}/>
		</IconButton>
	</Tooltip>
}
